import React from 'react';
import { ReviewStats } from '../services/api/reviewAutomation.service';

interface ReviewStatsCardProps {
  stats: ReviewStats;
}

export const ReviewStatsCard: React.FC<ReviewStatsCardProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Pending Changes */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Pending Review
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.total_pending}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-Approvable */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Auto-Approvable
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.auto_approvable}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Accepted */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Accepted
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.total_accepted}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Rejected */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Rejected
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {stats.total_rejected}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* By Entity Type */}
      {Object.keys(stats.by_entity_type).length > 0 && (
        <div className="bg-white overflow-hidden shadow rounded-lg md:col-span-2">
          <div className="p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-3">By Entity Type</h3>
            <div className="space-y-2">
              {Object.entries(stats.by_entity_type).map(([entityType, count]) => (
                <div key={entityType} className="flex justify-between">
                  <span className="text-sm text-gray-600 capitalize">{entityType}s</span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* By Category */}
      {Object.keys(stats.by_category).length > 0 && (
        <div className="bg-white overflow-hidden shadow rounded-lg md:col-span-2">
          <div className="p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-3">By Category</h3>
            <div className="space-y-2">
              {Object.entries(stats.by_category).map(([category, count]) => (
                <div key={category} className="flex justify-between">
                  <span className="text-sm text-gray-600 capitalize">
                    {category.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};